/**
 * Every customer receipt is laid out like the repair ticket: the store's header, a title, the
 * number big with its barcode under it, then the details. These pick the words for it.
 */
import { formatCents } from './money.js';
import { formatShortDate } from './parts.js';

/**
 * PAID or NOT PAID, in the same words on every printout (the repair tickets, the technician's
 * labels, the special order receipt): the stamp, and under it the receipt that paid it or what's
 * still due.
 */
export interface PayStamp {
  state: 'paid' | 'refunded' | 'unpaid' | 'free';
  /** PAID, NOT PAID, REFUNDED or NO CHARGE. */
  tag: string;
  /** "Receipt #100045", or "$244.00 due at pickup". */
  detail: string;
}

export function paymentStamp(o: {
  /** The receipt that paid it, when it's paid. */
  paid: { receipt_no: number | null; amount_cents?: number | null; refunded?: boolean } | null;
  /** What it costs: what's due while it isn't paid. */
  total_cents: number;
  /** False while there's no price yet (a repair waiting for its check). */
  priced?: boolean;
  /** 'now': paying when it's dropped off or ordered; otherwise at pickup. */
  pay_plan?: string | null;
}): PayStamp {
  if (o.paid) {
    const receipt = o.paid.receipt_no ? `Receipt #${o.paid.receipt_no}` : 'Paid at the counter';
    if (o.paid.refunded) return { state: 'refunded', tag: 'REFUNDED', detail: receipt };
    return { state: 'paid', tag: 'PAID', detail: o.paid.amount_cents ? `${receipt} · ${formatCents(o.paid.amount_cents)}` : receipt };
  }
  if (o.priced === false) return { state: 'unpaid', tag: 'NOT PAID', detail: 'Price after the check' };
  if (o.total_cents <= 0) return { state: 'free', tag: 'NO CHARGE', detail: 'Nothing to pay' };
  return { state: 'unpaid', tag: 'NOT PAID', detail: `${formatCents(o.total_cents)} due ${o.pay_plan === 'now' ? 'now' : 'at pickup'}` };
}

/**
 * A repair ticket's stamp. The receipt that paid it can hold other things too (a case), so its
 * total isn't printed here: the ticket's TOTAL above it is the repair's. A $0 ticket only says NO
 * CHARGE when that's final (a warranty repair, or the repair is done); before that the price can
 * still change after the check. Returned unrepaired and not paid: nothing is due.
 */
export function repairPayment(p: {
  /** `refunded`: every line the repair put on that receipt was refunded. */
  paid: { receipt_no: number | null; total_cents: number; refunded_cents?: number | null; refunded?: boolean } | null;
  totals: { total_cents: number };
  lines: unknown[];
  pay_plan?: string | null;
  status?: string | null;
  warranty?: boolean;
}): PayStamp {
  if (!p.paid && p.status === 'cancelled') return { state: 'free', tag: 'NOTHING DUE', detail: 'Returned, not repaired' };
  const refunded = !!p.paid && (p.paid.refunded ?? ((p.paid.refunded_cents ?? 0) > 0 && (p.paid.refunded_cents ?? 0) >= p.paid.total_cents));
  const final = !!p.warranty || p.status === 'ready' || p.status === 'picked_up';
  return paymentStamp({
    paid: p.paid ? { receipt_no: p.paid.receipt_no, refunded } : null,
    total_cents: p.totals.total_cents,
    priced: p.lines.length > 0 && (p.totals.total_cents > 0 || final),
    pay_plan: p.pay_plan,
  });
}

/** A special order's stamp: what it was paid on (its own amount), or what's due at pickup. */
export function orderPayment(p: {
  paid: { receipt_no: number | null; amount_cents: number | null; refunded: boolean } | null;
  total_cents: number;
  pay_plan?: string | null;
  status?: string | null;
}): PayStamp {
  if (!p.paid && p.status === 'cancelled') return { state: 'free', tag: 'NOTHING DUE', detail: 'Order cancelled' };
  return paymentStamp({ paid: p.paid, total_cents: p.total_cents, pay_plan: p.pay_plan });
}

/** On top of the special order receipt, so whoever reads it at the counter knows right away. */
export const ORDER_BANNER = 'PART ORDERED FOR THIS CUSTOMER';

/**
 * When the special order comes in, for the big box on its receipt: the expected day ("Thu, Oct
 * 15"), or that it's here, or (no date yet) that we'll let them know.
 */
export function orderArrival(o: { status?: string | null; expected: string | null }): { label: string; big: string | null; note: string | null } {
  if (o.status === 'arrived') return { label: 'IT ARRIVED', big: 'READY FOR PICKUP', note: null };
  if (o.status === 'picked_up') return { label: 'PICKED UP', big: null, note: null };
  if (o.status === 'cancelled') return { label: 'CANCELLED', big: null, note: null };
  if (o.expected) return { label: 'EXPECTED ARRIVAL', big: formatShortDate(o.expected).toUpperCase(), note: 'We’ll let you know when it’s here.' };
  return { label: 'EXPECTED ARRIVAL', big: null, note: 'We’ll let you know when it’s here.' };
}

// ---------------------------------------------------------------- the price sticker

/** A price as it goes on a sticker: "$499", or "$499.99" when there are cents. */
export function stickerPrice(cents: number): string {
  const s = formatCents(cents);
  return s.endsWith('.00') ? s.slice(0, -3) : s;
}

/** Carrier and lock, for the sticker: "Unlocked", "T-Mobile · Unlocked", "T-Mobile · LOCKED". */
export function carrierLock(d: { carrier: string | null; locked: boolean }): string {
  const carrier = (d.carrier ?? '').trim();
  if (!carrier || /^unlocked$/i.test(carrier)) return d.locked ? 'LOCKED' : 'Unlocked';
  return `${carrier} · ${d.locked ? 'LOCKED' : 'Unlocked'}`;
}

/** The phone's name on its sticker: no "Apple" in front of an iPhone. */
export function stickerModel(d: { brand: string; model: string; storage: string | null }): string {
  const model = d.model.toLowerCase().startsWith(d.brand.toLowerCase()) || d.brand === 'Apple' ? d.model : `${d.brand} ${d.model}`;
  return [model, d.storage].filter(Boolean).join(' ');
}

/**
 * What the price sticker printer (the Rollo) is loaded with (Settings → Printers): 3 × 2 in
 * stickers; 2 × 3 in labels (2 in across, 3 in long), on which the same sticker prints turned
 * sideways, one a label; 4 × 3 in labels, on which it prints a third bigger, sideways across
 * the label (4 in wide, 3 in tall); or a 2 × 2 in square (a smaller sticker on the top of a
 * 2 × 3 in label, for a printer driver with no 2 × 3 in paper: the printer feeds to the next label).
 */
export const PRICE_STICKER_SIZES = {
  '3x2': '3 × 2 in sticker (76 × 51 mm)',
  '2x3': '2 × 3 in label, sticker turned sideways (51 × 76 mm)',
  '2x2': '2 × 2 in square sticker (51 × 51 mm)',
  '4x3': '4 × 3 in label, sideways (102 × 76 mm)',
} as const;
export type PriceStickerSize = keyof typeof PRICE_STICKER_SIZES;
export const isPriceStickerSize = (v: unknown): v is PriceStickerSize => typeof v === 'string' && v in PRICE_STICKER_SIZES;
/** The size from the settings ("price_sticker_size"): 3 × 2 in unless the owner picked the 4 × 3 in label. */
export const priceStickerSize = (v: unknown): PriceStickerSize => (isPriceStickerSize(v) ? v : '3x2');

/**
 * How far right the price sticker prints, in mm (Settings → Printers → Move it right). The store's
 * Rollo prints a little to the left of the label and cut off the logo's W, so everything moves
 * right: the left margin grows by this much and the right margin gives way first. 0 to 20 mm,
 * like "10" or "10.5"; anything else is the default.
 */
export const PRICE_STICKER_SHIFT_MM = 10;
export const isPriceStickerShift = (v: unknown): v is string => typeof v === 'string' && /^\d{1,2}(\.\d)?$/.test(v.trim()) && Number(v) <= 20;
export const priceStickerShiftMm = (v: unknown): number => (isPriceStickerShift(v) ? Number(v) : PRICE_STICKER_SHIFT_MM);

/** The title on a sales receipt: a bill payment or an activation rung up by itself says so. */
export function saleReceiptTitle(lines: { kind?: string | null }[]): string {
  const kinds = new Set(lines.map((l) => l.kind).filter((k): k is string => !!k && k !== 'fee'));
  if (kinds.size === 1 && kinds.has('billpay')) return 'BILL PAYMENT RECEIPT';
  if (kinds.size === 1 && kinds.has('activation')) return 'ACTIVATION RECEIPT';
  return 'SALES RECEIPT';
}

/** The title on a phone bought from a customer, or a trade-in. */
export function buybackReceiptTitle(kind: string): string {
  return kind === 'trade_in' ? 'TRADE-IN RECEIPT' : 'BUY BACK RECEIPT';
}
